/**
 * Storage Service
 * Wrapper for localStorage/sessionStorage with optional encryption
 */

import CryptoJS from 'crypto-js';
import { SECURITY_CONFIG } from '@/api/config';

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'notifyy-default-key';

export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
}
class StorageService {
  private getStorage(type: StorageType): Storage | null {
    try {
      return type === StorageType.LOCAL ? localStorage : sessionStorage;
    } catch {
      return null;
    }
  }
  private encrypt(data: string): string {
    if (!SECURITY_CONFIG.ENABLE_ENCRYPTION) return data;
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
  }
  private decrypt(data: string): string {
    if (!SECURITY_CONFIG.ENABLE_ENCRYPTION) return data;
    try {
      const bytes = CryptoJS.AES.decrypt(data, ENCRYPTION_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return data;
    }
  }
  setItem<T>(
    key: string,
    value: T,
    encrypt = false,
    type = StorageType.LOCAL
  ): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;
    try {
      const stringValue = JSON.stringify(value);
      storage.setItem(key, encrypt ? this.encrypt(stringValue) : stringValue);
      return true;
    } catch {
      return false;
    }
  }

  getItem<T>(
    key: string,
    decrypt = false,
    type = StorageType.LOCAL
  ): T | null {
    const storage = this.getStorage(type);
    if (!storage) return null;
    try {
      const value = storage.getItem(key);
      if (!value) return null;
      const decrypted = decrypt ? this.decrypt(value) : value;
      return JSON.parse(decrypted) as T;
    } catch {
      return null;
    }
  }

  removeItem(key: string, type = StorageType.LOCAL): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;
    try {
      storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  clear(type = StorageType.LOCAL): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;
    try {
      storage.clear();
      return true;
    } catch {
      return false;
    }
  }

  hasItem(key: string, type = StorageType.LOCAL): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;
    try {
      return storage.getItem(key) !== null;
    } catch {
      return false;
    }
  }

  getAllKeys(type = StorageType.LOCAL): string[] {
    const storage = this.getStorage(type);
    if (!storage) return [];
    try {
      return Array.from({ length: storage.length }, (_, i) => storage.key(i))
        .filter((key): key is string => key !== null);
    } catch {
      return [];
    }
  }

  getSize(type = StorageType.LOCAL): number {
    const storage = this.getStorage(type);
    if (!storage) return 0;
    try {
      let size = 0;
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
          const value = storage.getItem(key);
          if (value) {
            size += (key.length + value.length) * 2; // UTF-16
          }
        }
      }
      return size;
    } catch {
      return 0;
    }
  }
}

export const storageService = new StorageService();
export const {
  setItem,
  getItem,
  removeItem,
  clear,
  hasItem,
  getAllKeys,
  getSize,
} = {
  setItem: storageService.setItem.bind(storageService),
  getItem: storageService.getItem.bind(storageService),
  removeItem: storageService.removeItem.bind(storageService),
  clear: storageService.clear.bind(storageService),
  hasItem: storageService.hasItem.bind(storageService),
  getAllKeys: storageService.getAllKeys.bind(storageService),
  getSize: storageService.getSize.bind(storageService),
};
export default storageService;
